import type {AddressMode, HalContext} from "@cm/hal/hal-context"
import type {HalImage} from "@cm/hal/hal-image/index"
import type {Box2Like, ColorLike, Size2Like, Vector2Like} from "@cm/math"
import {Box2, wrap} from "@cm/math"
import type {HalPaintable} from "@cm/hal/hal-paintable"
import {getHalAddressMode} from "#webgl2/utils"
import type {HalImageRef, HalPainterImageCompositorRef} from "@cm/hal/hal-ref"

export type ImageType = "physical" | "virtual"

export type CreateImageArgs = {
    width: number
    height: number
    type?: ImageType
}

export class TestContext {
    constructor(readonly halContext: HalContext) {
        this.testPatternCompositor = halContext.createHalPainterImageCompositor(`
            vec4 computeColor(ivec2 texelIndex) {
                return vec4(float(texelIndex.x), float(texelIndex.y), 0, 1);
            }
        `)
        this.fullTargetBlitCompositor = halContext.createHalPainterImageCompositor(`
            uniform int u_addressMode;
            
            vec4 computeColor(ivec2 texelIndex) {
                return texelFetch0(texelIndex, u_addressMode);
            }
        `)
    }

    dispose() {
        this.testPatternCompositor.release()
        this.fullTargetBlitCompositor.release()
    }

    createImage(args: CreateImageArgs) {
        const {width, height} = args
        const type = args.type ?? "physical"
        const createImageFn =
            type === "physical" ? this.halContext.createPhysicalImage.bind(this.halContext) : this.halContext.createVirtualImage.bind(this.halContext)
        return createImageFn({
            width: width,
            height: height,
            channelLayout: "RGBA",
            dataType: "float32",
        })
    }

    createConstantImage(args: CreateImageArgs & {constant: ColorLike | number}) {
        const image = this.createImage(args)
        const constant = args.constant
        this.fillImageWithConstant(image, constant)
        return image
    }

    fillImageWithConstant(image: HalImageRef, constant: ColorLike | number) {
        if (typeof constant === "number") {
            constant = {r: constant, g: constant, b: constant, a: constant}
        }
        image.ref.clear({
            color: constant,
        })
    }

    createTestPatternImage(args: CreateImageArgs) {
        const image = this.createImage(args)
        this.fillImageWithTestPattern(image)
        return image
    }

    fillImageWithTestPattern(image: HalImageRef) {
        this.testPatternCompositor.ref.paint({
            target: image.ref,
        })
    }

    blit(args: {target: HalPaintable; source: HalImage}) {
        this.halContext.blit({
            targetImage: args.target,
            sourceImage: args.source,
        })
    }

    blitFullTarget(args: {target: HalPaintable; source: HalImage; addressMode: AddressMode}) {
        this.fullTargetBlitCompositor.ref.paint({
            target: args.target,
            sourceImages: args.source,
            parameters: {
                u_addressMode: {type: "int", value: getHalAddressMode(args.addressMode)},
            },
        })
    }

    expectTestPattern(args: {image: HalImage; region?: Box2Like; expectedOffset?: Vector2Like; wrapSize?: Size2Like; gapSize?: Size2Like}) {
        const {image} = args
        if (image.descriptor.channelLayout !== "RGBA") {
            throw new Error("Expected image to have RGBA channel layout for test pattern comparison.")
        }
        const region = args.region ?? {
            x: 0,
            y: 0,
            width: image.descriptor.width,
            height: image.descriptor.height,
        }
        this.checkRegion(region)
        const expectedOffset = args.expectedOffset ?? {x: 0, y: 0}
        const data = image.readImageDataFloat({
            region,
        })
        const gapSize = args.gapSize ?? {width: 0, height: 0}
        for (let y = 0; y < region.height; y++) {
            for (let x = 0; x < region.width; x++) {
                const index = (y * region.width + x) * 4
                let expectedR = x + expectedOffset.x
                let expectedG = y + expectedOffset.y
                let expectedB = 0
                let expectedA = 1
                if (args.wrapSize) {
                    expectedR = wrap(expectedR, args.wrapSize.width + gapSize.width)
                    expectedG = wrap(expectedG, args.wrapSize.height + gapSize.height)
                    if (expectedR >= args.wrapSize.width || expectedG >= args.wrapSize.height) {
                        // we're in the gap area
                        expectedR = 0
                        expectedG = 0
                        expectedB = 0
                        expectedA = 0
                    }
                }
                const readR = data[index]
                const readG = data[index + 1]
                const readB = data[index + 2]
                const readA = data[index + 3]
                if (readR !== expectedR || readG !== expectedG || readB !== expectedB || readA !== expectedA) {
                    throw new Error(
                        `Mismatch at (${x + region.x}, ${y + region.y}): expected pattern entry (${expectedR}, ${expectedG}, ${expectedB}, ${expectedA}), got (${readR}, ${readG}, ${readB}, ${readA})`,
                    )
                }
            }
        }
    }

    expectConstant(args: {image: HalImage; region?: Box2Like; expectedConstant?: ColorLike | number; invertRegion?: boolean}) {
        const {image} = args
        const region = args.region ?? {
            x: 0,
            y: 0,
            width: image.descriptor.width,
            height: image.descriptor.height,
        }
        const invertRegion = args.invertRegion ?? false
        if (invertRegion) {
            const regions = this.invertRegion(region, image.descriptor)
            regions.forEach((region) => this.expectConstant({image, region, expectedConstant: args.expectedConstant}))
            return
        }
        this.checkRegion(region)
        let expectedConstant = args.expectedConstant ?? 0
        if (typeof expectedConstant === "number") {
            expectedConstant = {r: expectedConstant, g: expectedConstant, b: expectedConstant, a: expectedConstant}
        }
        if (image.descriptor.channelLayout !== "RGBA") {
            throw new Error("Expected image to have RGBA channel layout for test pattern comparison.")
        }
        const data = image.readImageDataFloat({
            region,
        })
        for (let y = 0; y < region.height; y++) {
            for (let x = 0; x < region.width; x++) {
                const index = (y * region.width + x) * 4
                const expectedR = expectedConstant.r
                const expectedG = expectedConstant.g
                const expectedB = expectedConstant.b
                const expectedA = expectedConstant.a
                const readR = data[index]
                const readG = data[index + 1]
                const readB = data[index + 2]
                const readA = data[index + 3]
                if (readR !== expectedR || readG !== expectedG || readB !== expectedB || readA !== expectedA) {
                    throw new Error(
                        `Mismatch at (${x + region.x}, ${y + region.y}): expected constant (${expectedR}, ${expectedG}, ${expectedB}, ${expectedA}), got (${readR}, ${readG}, ${readB}, ${readA})`,
                    )
                }
            }
        }
    }

    private checkRegion(region: Box2Like) {
        if (Box2.isEmpty(region)) {
            throw new Error("Region is empty, which hints at an error.")
        }
    }

    private invertRegion(region: Box2Like, imageSize: Size2Like) {
        const resultRegions: Box2Like[] = []
        const topRegion = {
            x: 0,
            y: 0,
            width: imageSize.width,
            height: region.y,
        }
        if (!Box2.isEmpty(topRegion)) {
            resultRegions.push(topRegion)
        }
        const bottomRegion = {
            x: 0,
            y: region.y + region.height,
            width: imageSize.width,
            height: imageSize.height - (region.y + region.height),
        }
        if (!Box2.isEmpty(bottomRegion)) {
            resultRegions.push(bottomRegion)
        }
        const leftRegion = {
            x: 0,
            y: region.y,
            width: region.x,
            height: region.height,
        }
        if (!Box2.isEmpty(leftRegion)) {
            resultRegions.push(leftRegion)
        }
        const rightRegion = {
            x: region.x + region.width,
            y: region.y,
            width: imageSize.width - (region.x + region.width),
            height: region.height,
        }
        if (!Box2.isEmpty(rightRegion)) {
            resultRegions.push(rightRegion)
        }
        if (resultRegions.length === 0) {
            throw new Error("The set of inverted regions is empty, which hints at an error.")
        }
        return resultRegions
    }

    private testPatternCompositor: HalPainterImageCompositorRef
    private fullTargetBlitCompositor: HalPainterImageCompositorRef
}
